import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { safeHtml } from "../lib/sanitize";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterStrip from "../components/NewsletterStrip";
import { PlayCircle, Clock } from "@phosphor-icons/react";

export default function VideosPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("vlogs"); // vlogs | shorts

  useEffect(() => {
    api.get("/videos?status=published&limit=100").then((r) => setItems(r.data.items));
  }, []);

  const vlogs = useMemo(() => items.filter((v) => v.is_vlog), [items]);
  const shorts = useMemo(() => items.filter((v) => !v.is_vlog), [items]);

  const featured = vlogs.find((v) => v.is_featured) || vlogs[0];
  const rest = vlogs.filter((v) => v !== featured);

  const showing = tab === "vlogs" ? rest : shorts;

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <Header />

      <section className="bg-[#1C1A17] text-[#F9F9F6]" data-testid="videos-hero">
        <div className="container-page py-14 md:py-20">
          <div className="overline text-[#D6CFC4]">Video Blog · JetsetLess Travels</div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[1.02] mt-4">
            The trips, the tradeoffs, on film.
          </h1>
          <p className="mt-5 text-[#D6CFC4] max-w-2xl">
            A long-form video series from the JetsetLess desk. Real itineraries, real points math, real cost breakdowns at the end of every episode.
          </p>
        </div>
      </section>

      {featured && (
        <section className="container-page py-14" data-testid="videos-featured">
          <Link to={`/video/${featured.slug}`} className="group grid md:grid-cols-12 gap-8 items-center" data-testid={`featured-video-${featured.slug}`}>
            <div className="md:col-span-7 img-zoom relative aspect-video overflow-hidden bg-[#EFECE5] border border-[#D6CFC4]">
              {featured.thumbnail && <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <PlayCircle size={72} weight="thin" className="absolute inset-0 m-auto text-white" />
              {featured.duration_seconds && (
                <div className="absolute bottom-3 right-3 bg-black/80 text-white text-xs font-mono px-2 py-1">
                  {fmtDuration(featured.duration_seconds)}
                </div>
              )}
            </div>
            <div className="md:col-span-5">
              <div className="overline text-[#B4533C]">
                {featured.season ? `S${featured.season} · ` : ""}{featured.episode_number ? `Episode ${featured.episode_number}` : "Featured episode"}
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight mt-3 group-hover:text-[#B4533C] transition-colors">
                {featured.title}
              </h2>
              <div className="mt-4 flex flex-wrap gap-3 text-xs font-mono text-[#4A4742]">
                {featured.host && <span>Hosted by {featured.host}</span>}
                {featured.location && <><span>·</span><span>{featured.location}</span></>}
                {featured.published_at && <><span>·</span><span>{new Date(featured.published_at).toLocaleDateString()}</span></>}
              </div>
              {featured.description && (
                <div className="prose-editorial mt-5 text-sm" dangerouslySetInnerHTML={safeHtml(truncateHtml(featured.description, 300))} />
              )}
              <span className="inline-flex items-center gap-2 mt-5 border border-[#1B3B36] text-[#1B3B36] px-5 py-2.5 text-sm tracking-wide group-hover:bg-[#1B3B36] group-hover:text-white transition-colors">
                <PlayCircle size={16} weight="regular" /> Watch episode
              </span>
            </div>
          </Link>
        </section>
      )}

      <section className="container-page pb-4">
        <div className="flex gap-2 border-b border-[#D6CFC4]">
          <TabBtn active={tab === "vlogs"} onClick={() => setTab("vlogs")} testId="tab-vlogs">Full episodes · {vlogs.length}</TabBtn>
          <TabBtn active={tab === "shorts"} onClick={() => setTab("shorts")} testId="tab-shorts">Shorts · {shorts.length}</TabBtn>
        </div>
      </section>

      <section className="container-page pb-16">
        {showing.length === 0 ? (
          <div className="border border-dashed border-[#D6CFC4] py-12 text-center text-sm text-[#4A4742]">No episodes in this feed yet.</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {showing.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
        )}
      </section>

      <NewsletterStrip />
      <Footer />
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-5 py-3 text-sm tracking-wide border-b-2 -mb-px ${active ? "border-[#B4533C] text-[#1C1A17] font-medium" : "border-transparent text-[#4A4742] hover:text-[#1C1A17]"}`}>
      {children}
    </button>
  );
}

function VideoCard({ v }) {
  return (
    <Link to={`/video/${v.slug}`} className="group block" data-testid={`video-card-${v.slug}`}>
      <div className="img-zoom relative aspect-video overflow-hidden bg-[#EFECE5] border border-[#D6CFC4]">
        {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <PlayCircle size={48} weight="thin" className="absolute inset-0 m-auto text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        {v.duration_seconds && (
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[11px] font-mono px-2 py-0.5">
            {fmtDuration(v.duration_seconds)}
          </div>
        )}
      </div>
      <div className="overline text-[#B4533C] mt-3">
        {v.episode_number ? `EP ${v.episode_number}` : v.format?.toUpperCase()}
        {v.location && ` · ${v.location}`}
      </div>
      <h3 className="font-serif text-xl leading-tight mt-1 group-hover:text-[#B4533C] transition-colors">{v.title}</h3>
      <div className="mt-2 flex items-center gap-2 text-xs font-mono text-[#4A4742]">
        {v.host}
        {v.published_at && <><span>·</span><span>{new Date(v.published_at).toLocaleDateString()}</span></>}
      </div>
    </Link>
  );
}

export function fmtDuration(sec) {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function truncateHtml(html, len) {
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > len ? text.slice(0, len).trim() + "…" : text;
}
